/**
 * AGC: Suggestion Detail Component
 * AGC: Shows a single suggestion with comments and allows adding new comments
 */

import React, { useState, useEffect } from 'react'
import { X, ArrowLeft, Send, MessageSquare, Clock } from 'lucide-react'
import { buildBackendUrl } from '../utils/backendUrl'

interface Comment {
  id: string
  text: string
  createdAt: number
  isAdmin?: boolean
}

interface Suggestion {
  id: string
  title: string
  category: string
  text: string
  createdAt: number
  updatedAt: number
  commentCount: number
  comments?: Comment[]
}

interface SuggestionDetailProps {
  suggestion: Suggestion
  onClose: () => void
  onBack: () => void
}

export const SuggestionDetail: React.FC<SuggestionDetailProps> = ({ suggestion: initialSuggestion, onClose, onBack }) => {
  const [suggestion, setSuggestion] = useState<Suggestion>(initialSuggestion)
  const [commentText, setCommentText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchSuggestionDetails()
    // AGC: eslint-disable-next-line - we only want to fetch when suggestion.id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion.id])

  const fetchSuggestionDetails = async () => {
    try {
      setIsLoading(true)
      const response = await fetch(buildBackendUrl(`api/suggestions/${suggestion.id}`))

      if (!response.ok) {
        throw new Error('Failed to fetch suggestion details')
      }

      const data = await response.json()
      setSuggestion(data.suggestion)
    } catch (err: any) {
      setError(err.message || 'Failed to load suggestion details')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!commentText.trim()) {
      return
    }

    if (commentText.length > 2000) {
      setError('Comment must be 2000 characters or less')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch(buildBackendUrl(`api/suggestions/${suggestion.id}/comments`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: commentText.trim() })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to add comment')
      }

      const data = await response.json()
      
      // AGC: Add comment to the list immediately (optimistic update)
      const newComment = {
        ...data.comment,
        createdAt: data.comment.createdAt || Date.now()
      }
      
      setSuggestion({
        ...suggestion,
        comments: [...(suggestion.comments || []), newComment],
        commentCount: (suggestion.commentCount || 0) + 1
      })

      setCommentText('')
      
      // AGC: Refresh the full suggestion to ensure we have the latest data
      await fetchSuggestionDetails()
    } catch (err: any) {
      setError(err.message || 'Failed to add comment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatDate = (timestamp: number) => {
    try {
      return new Date(timestamp).toLocaleString()
    } catch {
      return 'Unknown date'
    }
  }

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Token': 'bg-yellow-500/20 text-yellow-400',
      'Game Mechanics': 'bg-blue-500/20 text-blue-400',
      'Blockchain': 'bg-purple-500/20 text-purple-400',
      'UI/UX': 'bg-pink-500/20 text-pink-400',
      'Buffs & NFTs': 'bg-green-500/20 text-green-400',
      'Battle System': 'bg-red-500/20 text-red-400',
      'Other': 'bg-gray-500/20 text-gray-400'
    }
    return colors[category] || colors['Other']
  }

  return (
    <div className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full mx-auto max-h-[90vh] flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Back to list"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h2 className="text-2xl font-bold text-white">Suggestion Details</h2>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-400">Loading...</span>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200 mb-4">
          {error}
        </div>
      ) : (
        <>
          {/* AGC: Suggestion Content */}
          <div className="flex-1 overflow-y-auto space-y-6 mb-6">
            <div className="bg-slate-700 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-2xl font-bold text-white">{suggestion.title}</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(suggestion.category)}`}>
                  {suggestion.category}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>Posted {formatDate(suggestion.createdAt)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <MessageSquare className="w-4 h-4" />
                  <span>{suggestion.commentCount || 0} comments</span>
                </div>
              </div>
              <div className="text-gray-200 whitespace-pre-wrap leading-relaxed">
                {suggestion.text}
              </div>
            </div>

            {/* AGC: Comments Section */}
            <div>
              <h4 className="text-xl font-bold text-white mb-4">
                Comments ({suggestion.comments?.length || 0})
              </h4>

              {suggestion.comments && suggestion.comments.length > 0 ? (
                <div className="space-y-4">
                  {suggestion.comments.map((comment) => (
                    <div
                      key={comment.id}
                      className={`rounded-lg p-4 ${
                        comment.isAdmin
                          ? 'bg-blue-900/30 border-2 border-blue-500/50'
                          : 'bg-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {comment.isAdmin && (
                          <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded flex items-center gap-1">
                            <span>🛡️</span>
                            ADMIN
                          </span>
                        )}
                      </div>
                      <div className="text-gray-200 whitespace-pre-wrap mb-2">{comment.text}</div>
                      <div className="text-xs text-gray-400">{formatDate(comment.createdAt)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              )}
            </div>
          </div>

          {/* AGC: Add Comment Form */}
          <div className="border-t border-slate-700 pt-6 mt-6">
            <form onSubmit={handleAddComment} className="space-y-3">
              {error && (
                <div className="p-3 bg-red-900/50 border border-red-500 rounded-lg text-red-200 text-sm">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="comment" className="block text-sm font-semibold text-gray-300 mb-2">
                  Add a Comment
                </label>
                <textarea
                  id="comment"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  maxLength={2000}
                  rows={4}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Share your thoughts..."
                  disabled={isSubmitting}
                />
                <div className="text-xs text-gray-400 mt-1">{commentText.length}/2000 characters</div>
              </div>
              <button
                type="submit"
                disabled={isSubmitting || !commentText.trim()}
                className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Adding comment...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Add Comment
                  </>
                )}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  )
}

